/**
 * apiService.ts
 *
 * PURPOSE:
 * --------
 * MFA / Forgot Username API layer.
 * Pre-login flow with HMAC security.
 */

import axios from "axios";
import { environment } from "../config/environment.ts";
import { signRequest } from "../utils/api/hmacClient-newlibrary.ts";

const HMAC_VALUE = import.meta.env.VITE_PRELOGIN_HMAC_VALUE;
const API_KEY = import.meta.env.VITE_X_API_KEY;

const PATH_PREFIX = "/api/v1/member/forgot-username";
const API_URL = `${environment.api_gateway_url}${PATH_PREFIX}`;
const AUTHORIZER = "member-portal-pre-login";

/**
 * Base error messages (UI-safe)
 */
const SOMETHING_WENT_WRONG = "Something went wrong.";
const SERVER_ERROR = "Server error.";
const ACCOUNT_ERROR = "Account not found. Please double check your information.";

/**
 * Map backend error codes → user-friendly messages
 * Appended to the generic message for better UX
 */
const mapErrorCodeToMessage = (code?: string): string => {
  switch (code) {
    case "INVALID_OTP":
      return "The code you entered is incorrect.";
    case "OTP_EXPIRED":
      return "Your code has expired. Please request a new one.";
    case "OTP_NOT_FOUND":
      return "No verification code found. Please request a new one.";
    case "INVALID_PHONE":
      return "Invalid phone number.";
    case "MISSING_CONTACT":
      return "Missing contact information.";
    case "USER_NOT_FOUND":
      return ACCOUNT_ERROR;
    case "EMAIL_SEND_FAILED":
    case "SMS_SEND_FAILED":
    case "SMS_VERIFY_FAILED":
    case "AUTH0_LOOKUP_FAILED":
      return "Service temporarily unavailable. Please try again.";
    default:
      return "";
  }
};

/**
 * Safely extract and normalize API errors
 * Combines generic + specific message
 */
const handleApiError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const code = error.response?.data?.code;
    const apiMessage = error.response?.data?.message;

    const mappedMessage = (code === 'SMS_VERIFY_FAILED' || code === 'SMS_SEND_FAILED') && apiMessage?.toLowerCase().includes('max')
      ? "You've reached the maximum verification attempts. Please try after some time"
      : mapErrorCodeToMessage(code);

    // Logging for debugging (safe: no sensitive data)
    console.error("[API ERROR]", {
      url: error.config?.url,
      status: error.response?.status,
      code,
      apiMessage,
    });

    return {
      code: code || "INTERNAL_SERVER_ERROR",
      message: mappedMessage || `${SOMETHING_WENT_WRONG} ${apiMessage || SERVER_ERROR}`.trim(),
    };
  }

  if (error instanceof Error) {
    console.error("[UNEXPECTED ERROR]", error.message);
    return {
      code: "INTERNAL_SERVER_ERROR",
      message: `${SOMETHING_WENT_WRONG} ${error.message}`,
    };
  }

  console.error("[UNKNOWN ERROR]", error);
  return {
    code: "INTERNAL_SERVER_ERROR",
    message: SOMETHING_WENT_WRONG,
  };
};

type OtpPayload =
  | { contact: string; otp: string; method: "email" }
  | { contact: string; email: string; otp: string; method: "sms" };

/**
 * Helper: build headers with HMAC
 */
const buildHeaders = (path: string, transactionId: string, authorizer?: string) => {
  const { timestamp, signature } = signRequest("POST", path, HMAC_VALUE);

  return {
    Authorization: authorizer || AUTHORIZER,
    "x-api-key": API_KEY,
    "x-timestamp": timestamp,
    "x-signature": signature,
    "x-ent-mbr-txn-id": transactionId,
    "Content-Type": "application/json",
  };
};

/**
 * Get MFA factors
 */
export const getMfaFactors = async (
  formData: { lastName: string; dob: string; email: string },
  transactionId: string
) => {
  try {
    const response = await axios.post(`${API_URL}/get-mfa`, formData, {
      headers: buildHeaders(`${PATH_PREFIX}/get-mfa`, transactionId),
    });

    return { data: response.data, error: null };
  } catch (error) {
    const err = handleApiError(error);

    return {
      data: null,
      error: err.code === "USER_NOT_FOUND" ? ACCOUNT_ERROR : err.message,
    };
  }
};

/**
 * Sends OTP to the specified contact using method "email" or "sms"
 */
export const sendOtp = async (
  contact: string, method: "sms" | "email",
  channel: "sms" | "call" = "sms",
  transactionId: string
) => {
  try {
    const response = await axios.post(
      `${API_URL}/send-otp`,
      { contact, method, channel },
      {
        headers: buildHeaders(`${PATH_PREFIX}/send-otp`, transactionId),
      },
    );

    return { data: response.data, error: null };
  } catch (error) {
    const err = handleApiError(error);

    return { data: null, error: err.message };
  }
};

/**
 * Verifies the OTP sent to the contact using "email" or "sms"
 */
export const verifyOtp = async (payload: OtpPayload, transactionId: string) => {
  try {
    const response = await axios.post(`${API_URL}/verify-otp`, payload, {
      headers: buildHeaders(`${PATH_PREFIX}/verify-otp`, transactionId),
    });

    return { data: response.data, error: null };
  } catch (error) {
    const err = handleApiError(error);

    return { data: null, error: err.message };
  }
};
